import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Button, Grid, Alert, CircularProgress, Chip, Collapse,
} from '@mui/material';
import { Save, RestartAlt, ExpandMore, ExpandLess } from '@mui/icons-material';
import { UNIT_OPTIONS, getDefaultUnit } from '../utils/unitConversions';
import { findMetricByInternal } from '../config/sensorMetrics';
import { stationService } from '../services/api';

interface UnitPreferencesSectionProps {
  stationUuid: string;
  currentPreferences: Record<string, string> | null;
  onSaved: (prefs: Record<string, string>) => void;
}

const GROUPED_CONVERTIBLE = [
  {
    category: 'Temperature',
    metrics: ['temperature_celsius', 'dew_point_celsius', 'soil_temperature_10cm_celsius',
      'soil_temperature_20cm_celsius', 'soil_temperature_30cm_celsius',
      'soil_temperature_50cm_celsius', 'soil_temperature_100cm_celsius',
      'leaf_temperature_celsius', 'water_temperature_celsius', 'road_surface_temp_celsius'],
  },
  { category: 'Wind', metrics: ['wind_speed_m_per_s', 'wind_gust_m_per_s'] },
  { category: 'Speed', metrics: ['vehicle_speed_avg_kmh'] },
  { category: 'Pressure', metrics: ['atmospheric_pressure_mbar'] },
  { category: 'Precipitation', metrics: ['rain_mm', 'reference_et_mm'] },
  { category: 'Distance', metrics: ['visibility_km', 'water_level_m'] },
  { category: 'Flow', metrics: ['water_flow_m3s'] },
  { category: 'Radiation', metrics: ['solar_radiation_watt_per_sqm'] },
];

const UnitPreferencesSection: React.FC<UnitPreferencesSectionProps> = ({
  stationUuid, currentPreferences, onSaved
}) => {
  const [prefs, setPrefs] = useState<Record<string, string>>(currentPreferences || {});
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  useEffect(() => {
    setPrefs(currentPreferences || {});
  }, [currentPreferences]);

  const toggleCategory = (cat: string) => {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(cat)) next.delete(cat); else next.add(cat);
      return next;
    });
  };

  const handleChange = (metric: string, unit: string) => {
    const defaultUnit = getDefaultUnit(metric);
    setPrefs(prev => {
      const next = { ...prev };
      if (unit === defaultUnit) {
        delete next[metric];
      } else {
        next[metric] = unit;
      }
      return next;
    });
  };

  const handleSave = async () => {
    setSaving(true); setError(''); setSuccess('');
    try {
      await stationService.update(stationUuid, { unit_preferences: prefs });
      onSaved(prefs);
      setSuccess('Unit preferences saved');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setPrefs({});
  };

  const selectStyle = {
    padding: '6px 8px', fontSize: '0.85rem',
    background: '#1a1a2e', color: '#fff',
    border: '1px solid rgba(255,255,255,0.23)',
    borderRadius: 4, cursor: 'pointer',
  };

  // Count how many metrics have non-default unit in each category
  const getCategoryChangeCount = (metrics: string[]) => {
    return metrics.filter(m => prefs[m]).length;
  };

  return (
    <Box>
      <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
        Display Units
      </Typography>
      <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.5)', mb: 2 }}>
        Choose how measurements are displayed for this station. Data is always stored in standard units.
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

      {GROUPED_CONVERTIBLE.map(group => {
        const isExpanded = expanded.has(group.category);
        const changeCount = getCategoryChangeCount(group.metrics);

        return (
          <Box key={group.category} sx={{ mb: 1 }}>
            <Box
              onClick={() => toggleCategory(group.category)}
              sx={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                p: 1.5, borderRadius: 1, cursor: 'pointer',
                background: 'rgba(0,212,255,0.06)', border: '1px solid rgba(0,212,255,0.12)',
                '&:hover': { background: 'rgba(0,212,255,0.1)' },
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#00d4ff' }}>
                  {group.category}
                </Typography>
                <Chip label={`${group.metrics.length}`} size="small"
                  sx={{ height: 20, fontSize: '0.7rem', background: 'rgba(0,212,255,0.15)', color: '#00d4ff' }} />
                {changeCount > 0 && (
                  <Chip label={`${changeCount} changed`} size="small"
                    sx={{ height: 20, fontSize: '0.7rem', background: 'rgba(255,152,0,0.15)', color: '#ff9800' }} />
                )}
              </Box>
              {isExpanded ? <ExpandLess sx={{ color: 'rgba(255,255,255,0.4)' }} /> : <ExpandMore sx={{ color: 'rgba(255,255,255,0.4)' }} />}
            </Box>

            <Collapse in={isExpanded}>
              <Grid container spacing={1} sx={{ mt: 0.5 }}>
                {group.metrics.map(metric => {
                  const options = UNIT_OPTIONS[metric];
                  if (!options) return null;
                  const metricDef = findMetricByInternal(metric);
                  const label = metricDef
                    ? metricDef.userFriendly.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')
                    : metric;
                  const isChanged = !!prefs[metric];

                  return (
                    <Grid item xs={12} sm={6} md={4} key={metric}>
                      <Box sx={{
                        display: 'flex', alignItems: 'center', gap: 1, p: 1,
                        background: isChanged ? 'rgba(255,152,0,0.08)' : 'rgba(0,0,0,0.15)',
                        border: isChanged ? '1px solid rgba(255,152,0,0.2)' : '1px solid transparent',
                        borderRadius: 1,
                      }}>
                        <Typography variant="caption" sx={{ flex: 1, color: 'rgba(255,255,255,0.7)', fontSize: '0.8rem' }} noWrap>
                          {label}
                        </Typography>
                        <select
                          value={prefs[metric] || options[0].unit}
                          onChange={e => handleChange(metric, e.target.value)}
                          style={{ ...selectStyle, minWidth: 80 }}
                        >
                          {options.map(o => (
                            <option key={o.unit} value={o.unit}>{o.unit}</option>
                          ))}
                        </select>
                      </Box>
                    </Grid>
                  );
                })}
              </Grid>
            </Collapse>
          </Box>
        );
      })}

      <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
        <Button variant="contained" onClick={handleSave} disabled={saving}
          startIcon={saving ? <CircularProgress size={16} /> : <Save />}
          sx={{ background: 'linear-gradient(135deg, #00d4ff, #0099cc)' }}>
          {saving ? 'Saving...' : 'Save Preferences'}
        </Button>
        <Button variant="outlined" onClick={handleReset} startIcon={<RestartAlt />}
          sx={{ borderColor: 'rgba(255,255,255,0.2)', color: 'rgba(255,255,255,0.5)' }}>
          Reset to Default
        </Button>
      </Box>
    </Box>
  );
};

export default UnitPreferencesSection;
